export type SellerFaqRole = "partner" | "host";
export type SellerFaqPayment = "all" | "in_loco" | "online";

export type SellerFaqItemId =
  | "accessHow"
  | "accessCredentials"
  | "accessCollaborator"
  | "accessClose"
  | "accessApp"
  | "activitiesAdd"
  | "activitiesEdit"
  | "activitiesDescription"
  | "activitiesLanguages"
  | "activitiesAvailability"
  | "activitiesDelete"
  | "activitiesPhotos"
  | "bookingsHandshake"
  | "bookingsNotifications"
  | "bookingsNoConfirm"
  | "bookingsLeadTime"
  | "bookingsCancel"
  | "bookingsReschedule"
  | "bookingsGuestId"
  | "bookingsContactGuest"
  | "bookingsOverbooking"
  | "commissions"
  | "paymentsOnSite"
  | "paymentsReceiptWhy"
  | "paymentsReceiptHow"
  | "walletPayouts"
  | "walletReports"
  | "tabletsHow"
  | "tabletsOwnership"
  | "tabletsDuty"
  | "tabletsBroken"
  | "tabletsDamage"
  | "tabletsEarnings"
  | "tabletsGuestIssue"
  | "tabletsOwnActivities"
  | "tabletsReturn"
  | "supportTicket";

export type SellerFaqItemConfig = {
  id: SellerFaqItemId;
  categoryKey: keyof typeof SELLER_FAQ_CATEGORY_KEYS;
  roles: SellerFaqRole[];
  payment: SellerFaqPayment;
};

export const SELLER_FAQ_CATEGORY_KEYS = {
  accessAccount: "categories.accessAccount",
  myActivities: "categories.myActivities",
  bookings: "categories.bookings",
  commissions: "categories.commissions",
  onSitePayments: "categories.onSitePayments",
  wallet: "categories.wallet",
  tablets: "categories.tablets",
  support: "categories.support",
} as const;

/** Order and visibility metadata from FAQ-Portale-Flitrip.pdf (issue #512). */
export const SELLER_FAQ_ITEMS: SellerFaqItemConfig[] = [
  {
    id: "accessHow",
    categoryKey: "accessAccount",
    roles: ["partner", "host"],
    payment: "all",
  },
  {
    id: "accessCredentials",
    categoryKey: "accessAccount",
    roles: ["partner", "host"],
    payment: "all",
  },
  {
    id: "accessCollaborator",
    categoryKey: "accessAccount",
    roles: ["partner", "host"],
    payment: "all",
  },
  {
    id: "accessClose",
    categoryKey: "accessAccount",
    roles: ["partner", "host"],
    payment: "all",
  },
  {
    id: "accessApp",
    categoryKey: "accessAccount",
    roles: ["partner", "host"],
    payment: "all",
  },
  {
    id: "activitiesAdd",
    categoryKey: "myActivities",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "activitiesEdit",
    categoryKey: "myActivities",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "activitiesDescription",
    categoryKey: "myActivities",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "activitiesLanguages",
    categoryKey: "myActivities",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "activitiesAvailability",
    categoryKey: "myActivities",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "activitiesDelete",
    categoryKey: "myActivities",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "activitiesPhotos",
    categoryKey: "myActivities",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "bookingsHandshake",
    categoryKey: "bookings",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "bookingsNotifications",
    categoryKey: "bookings",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "bookingsNoConfirm",
    categoryKey: "bookings",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "bookingsLeadTime",
    categoryKey: "bookings",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "bookingsCancel",
    categoryKey: "bookings",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "bookingsReschedule",
    categoryKey: "bookings",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "bookingsGuestId",
    categoryKey: "bookings",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "bookingsContactGuest",
    categoryKey: "bookings",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "bookingsOverbooking",
    categoryKey: "bookings",
    roles: ["partner"],
    payment: "all",
  },
  {
    id: "commissions",
    categoryKey: "commissions",
    roles: ["partner", "host"],
    payment: "all",
  },
  {
    id: "paymentsOnSite",
    categoryKey: "onSitePayments",
    roles: ["partner"],
    payment: "in_loco",
  },
  {
    id: "paymentsReceiptWhy",
    categoryKey: "onSitePayments",
    roles: ["partner"],
    payment: "in_loco",
  },
  {
    id: "paymentsReceiptHow",
    categoryKey: "onSitePayments",
    roles: ["partner"],
    payment: "in_loco",
  },
  {
    id: "walletPayouts",
    categoryKey: "wallet",
    roles: ["partner", "host"],
    payment: "online",
  },
  {
    id: "walletReports",
    categoryKey: "wallet",
    roles: ["partner", "host"],
    payment: "online",
  },
  {
    id: "tabletsHow",
    categoryKey: "tablets",
    roles: ["host"],
    payment: "all",
  },
  {
    id: "tabletsOwnership",
    categoryKey: "tablets",
    roles: ["host"],
    payment: "all",
  },
  {
    id: "tabletsDuty",
    categoryKey: "tablets",
    roles: ["host"],
    payment: "all",
  },
  {
    id: "tabletsBroken",
    categoryKey: "tablets",
    roles: ["host"],
    payment: "all",
  },
  {
    id: "tabletsDamage",
    categoryKey: "tablets",
    roles: ["host"],
    payment: "all",
  },
  {
    id: "tabletsEarnings",
    categoryKey: "tablets",
    roles: ["host"],
    payment: "all",
  },
  {
    id: "tabletsGuestIssue",
    categoryKey: "tablets",
    roles: ["host"],
    payment: "all",
  },
  {
    id: "tabletsOwnActivities",
    categoryKey: "tablets",
    roles: ["host"],
    payment: "all",
  },
  {
    id: "tabletsReturn",
    categoryKey: "tablets",
    roles: ["host"],
    payment: "all",
  },
  {
    id: "supportTicket",
    categoryKey: "support",
    roles: ["partner", "host"],
    payment: "all",
  },
];
